import React, { useEffect, useRef } from "react";

import { BadgeCheck, Clock, User } from "lucide-react";

import { PlanNav, shortDate } from "./shared";
import type {
  AccountPlanOverview,
  SectionOwner,
  Verification,
} from "../../Types/AccountPlan";

/** Section title by id (s1…s9), falling back to the id. */
export const sectionTitle = (data: AccountPlanOverview, sid: string): string =>
  data.sections.find(s => s.id === sid)?.title || sid;

export const sectionTitles = (
  data: AccountPlanOverview,
  ids: string[]
): string => ids.map(id => sectionTitle(data, id)).join(", ");

/** "Verified by AE · Sep 28", "… · unchanged" (carried), or the pending mark. */
export const VerifyMark: React.FC<{
  sid: string;
  owner: SectionOwner | null | undefined;
  verification?: Verification | null;
}> = ({ sid, owner, verification: v }) => {
  if (!owner) return null;
  const base =
    "inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium";
  if (v) {
    return (
      <span
        data-testid={`verify-mark-${sid}`}
        title={
          v.carried
            ? "Section unchanged since an earlier verified version — verification carried forward"
            : v.name
              ? `Verified by ${v.name}`
              : undefined
        }
        className={`${base} ${
          v.carried
            ? "border-gray-200 bg-gray-100 text-gray-600"
            : "border-emerald-200 bg-emerald-50 text-emerald-700"
        }`}
      >
        <BadgeCheck className="h-3 w-3" aria-hidden />
        Verified by {v.role} · {shortDate(v.at)}
        {v.carried ? " · unchanged" : ""}
      </span>
    );
  }
  return (
    <span
      data-testid={`verify-mark-${sid}`}
      className={`${base} border-amber-200 bg-amber-50 text-amber-800`}
    >
      <Clock className="h-3 w-3" aria-hidden />
      {owner === "CSM" ? "CSM confirmation pending" : "AE verification pending"}
    </span>
  );
};

export const OwnerChip: React.FC<{ owner: SectionOwner }> = ({ owner }) => (
  <span
    className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${
      owner === "CSM"
        ? "bg-teal-50 text-teal-700"
        : "bg-indigo-50 text-indigo-700"
    }`}
  >
    <User className="h-2.5 w-2.5" aria-hidden />
    Owned by {owner}
  </span>
);

/** AE/CSM lens switch between the draft briefing and the briefing questions. */
export const ReviewLensToggle: React.FC<{
  active: "briefing" | "questions";
  nav: PlanNav;
}> = ({ active, nav }) => {
  const item = (
    key: "briefing" | "questions",
    label: string,
    tab: "sponsor" | "qa"
  ) => (
    <button
      type="button"
      aria-pressed={active === key}
      onClick={() => active !== key && nav.go(tab)}
      className={`rounded-md px-2.5 py-1 text-xs font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50 ${
        active === key
          ? "bg-white text-gray-900 shadow-sm"
          : "text-gray-500 hover:text-gray-800"
      }`}
    >
      {label}
    </button>
  );
  return (
    <div
      role="group"
      aria-label="Review lens"
      className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5"
    >
      {item("briefing", "Briefing", "sponsor")}
      {item("questions", "Briefing Questions", "qa")}
    </div>
  );
};

/** Confirm modal for "Verify all (N)". */
export const VerifyAllModal: React.FC<{
  titles: string[];
  today: string;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}> = ({ titles, today, busy, onCancel, onConfirm }) => {
  const confirmRef = useRef<HTMLButtonElement>(null);
  const n = titles.length;

  useEffect(() => {
    confirmRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 p-4"
      onMouseDown={e => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="verify-all-title"
        className="w-full max-w-md rounded-xl border border-gray-200 bg-white p-5 shadow-xl"
      >
        <div className="font-mono text-[10px] uppercase tracking-widest text-gray-400">
          Verify all your sections
        </div>
        <h2
          id="verify-all-title"
          className="mb-3 mt-1 text-lg font-semibold text-gray-900"
        >
          Verify {n} sections?
        </h2>
        <ul className="mb-4 space-y-1.5 text-sm text-gray-700">
          {titles.map(t => (
            <li key={t} className="flex items-center gap-2">
              <BadgeCheck className="h-4 w-4 text-purple-500" aria-hidden />
              {t}
            </li>
          ))}
        </ul>
        <p className="mb-5 text-xs text-gray-500">
          {`The sponsor sees "Verified by AE · ${today}" on each. Only verify sections you've read.`}
        </p>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50"
          >
            Cancel
          </button>
          <button
            ref={confirmRef}
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-lg bg-[#974ae4] px-3 py-1.5 text-sm font-semibold text-white hover:bg-[#8538d4] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50 disabled:opacity-50"
          >
            <BadgeCheck className="h-4 w-4" aria-hidden />
            Verify {n} sections
          </button>
        </div>
      </div>
    </div>
  );
};

export default VerifyMark;
